import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Divider,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual, sortBy, startCase } from "lodash";
import { useCallback, useMemo, useState } from "react";

import Help from "~/components/common/Help";
import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import { useOrgConfig, VisitResourceRequirement, VisitType } from "~/providers/OrgConfigProvider";

type VisitResourceRequirementProps = {
  visitType: VisitType;
  visitResourceRequirement: VisitResourceRequirement;
};

const offsetOptions = [0, 5, 10, 15, 20, 25, 30];

export const durationOptions = [
  [10, "10 min"],
  [15, "15 min"],
  [20, "20 min"],
  [30, "30 min"],
  [40, "40 min"],
  [45, "45 min"],
  [60, "60 min"],
  [75, "75 min"],
  [90, "90 min"],
  [120, "120 min"],
];

const labelSize = { xs: 12, md: 5, lg: 4 };
const inputSize = { xs: 12, md: 7, lg: 8 };

const VisitResourceRequirementFormRow = ({ visitResourceRequirement, visitType }: VisitResourceRequirementProps) => {
  const baseVRR = useMemo(() => ({
    visitResourceRequirement: visitResourceRequirement.id,
    duration: visitResourceRequirement.duration,
    inHome: visitResourceRequirement.inHome,
    invitedToVisit: visitResourceRequirement.invitedToVisit,
    offset: visitResourceRequirement.offset,
    requiresContinuityOfCare: visitResourceRequirement.requiresContinuityOfCare,
    preserveAutoschedulerGroup: visitResourceRequirement.preserveAutoschedulerGroup,
    providerTypeIds: visitResourceRequirement.providerTypes.map(pt => pt.id),
  }), [visitResourceRequirement]);

  const [local, setLocal] = useState(baseVRR);
  const { providerTypes } = useOrgConfig();

  const [updateVisitResourceRequirement, { loading }] = useMutation(UPDATE_VISIT_RESOURCE_REQUIREMENT);

  const onSave = useCallback(() => {
    updateVisitResourceRequirement({
      variables: local,
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  }, [local]);

  const noLocalChanges = isEqual(local, baseVRR);

  const hasOtherInHomeProvider = !!visitType.visitResourceRequirements.find(vrr => vrr.inHome && vrr.id !== local.visitResourceRequirement);

  return <Grid container sx={{ alignItems: "center" }} rowSpacing={1}>
    <Grid size={12}>
      <Divider sx={{ mb: 1 }}></Divider>
    </Grid>

    <Grid size={labelSize}>
      Provider Types
      <Help text={`Which provider types can be selected for this part of the visit`} />
    </Grid>
    <Grid size={inputSize}>
      <CheckboxSelect
        value={local.providerTypeIds}
        onChange={providerTypeIds => setLocal({ ...local, providerTypeIds})}
        options={sortBy(Object.values(providerTypes).map(pt => ({ key: pt.id, label: startCase(pt.name)})), "label")}
        sx={{ width: "90%" }}
        required
      />
    </Grid>

    <Grid size={labelSize}>
      In-Home
      <Help text={`If the provider is in home with the patient.`} />
    </Grid>
    <Grid size={inputSize}>
      <Switch
        checked={local.inHome}
        aria-label="In-Home"
        disabled={loading}
        onChange={(event) => { setLocal({
          ...local,
          inHome: event.target.checked,
          // If marking them as in-home, they cannot be invitedToVisit. Unset it.
          invitedToVisit: event.target.checked ? false : local.invitedToVisit,
        })}}
      />
    </Grid>

    {(!local.inHome || local.invitedToVisit) && <>
      <Grid size={labelSize} sx={{pl: 2}}>
        Invited to visit
        <Help text={`The in-home provider invites this provider to join the visit via the mobile app. This provider is then notified in the web app that the patient is ready for them.`} />
      </Grid>
      <Grid size={inputSize}>
        <Switch
          checked={local.invitedToVisit}
          aria-label="Invited to Visit"
          disabled={loading || (!hasOtherInHomeProvider && !local.invitedToVisit)}
          onChange={(event) => { setLocal({...local, invitedToVisit: event.target.checked })}}
        />
      </Grid>
    </>}

    <Grid size={labelSize}>
      Offset
      <Help text={`How long after the start of the visit the provider will join in`} />
    </Grid>
    <Grid size={inputSize}>
      <TextField
        select
        slotProps={{ htmlInput: { "data-testid": "offset-dropdown" }}}
        size="small"
        sx={{ width: "8em" }}
        value={local.offset}
        onChange={e => setLocal({ ...local, offset: parseInt(e.target.value)})}
      >
        {offsetOptions.map((option) => <MenuItem key={option} value={option.toString()}>{option} min</MenuItem>)}
      </TextField>
    </Grid>

    <Grid size={labelSize}>
      Duration
      <Help text={`How long how long the provider will join the visit. Additional services may change the visit length.`} />
    </Grid>
    <Grid size={inputSize}>
      <TextField
        select
        slotProps={{ htmlInput: { "data-testid": "duration-dropdown" }}}
        size="small"
        sx={{ width: "8em" }}
        value={local.duration}
        onChange={e => setLocal({ ...local, duration: parseInt(e.target.value)})}
      >
        {durationOptions.map((option) => <MenuItem key={option[0]} value={option[0].toString()}>{option[1]}</MenuItem>)}
      </TextField>
    </Grid>

    <Grid size={labelSize}>
      Assign Care Team
      <Help text={`When scheduling a visit with a patient, filter options to the same provider as last time.`} />
    </Grid>
    <Grid size={inputSize}>
      <Switch
        checked={local.requiresContinuityOfCare}
        disabled={loading}
        aria-label="Assign Care Team"
        onChange={(event) => { setLocal({...local, requiresContinuityOfCare: event.target.checked })}}
      />
    </Grid>

    <Grid size={labelSize}>
      Preserve Group
      <Help text={`When autoscheduling, keep visit resources with this requirement grouped together.`} />
    </Grid>
    <Grid size={inputSize}>
      <Switch
        checked={local.preserveAutoschedulerGroup}
        disabled={loading}
        aria-label="Preserve Group"
        onChange={(event) => { setLocal({ ...local, preserveAutoschedulerGroup: event.target.checked })}}
      />
    </Grid>

    <Grid>
      <Button variant="contained" size="small" color="primary" disabled={noLocalChanges || local.providerTypeIds.length === 0} onClick={onSave} loading={loading}>
        Save
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={() => setLocal(baseVRR)}
        sx={{ ml: 1 }}
      >
        Reset
      </Button>
    </Grid>
  </Grid>
}

export default VisitResourceRequirementFormRow;

const UPDATE_VISIT_RESOURCE_REQUIREMENT = gql(`
  mutation UpdateVisitResourceRequirement(
    $visitResourceRequirement: Int!
    $duration: Int!
    $inHome: Boolean!
    $invitedToVisit: Boolean!
    $offset: Int!
    $requiresContinuityOfCare: Boolean!
    $preserveAutoschedulerGroup: Boolean!
    $providerTypeIds: [Int!]!
  )
  @permission(requires: manageOrgConfig)
  {
    updateVisitResourceRequirement(
      id: $visitResourceRequirement
      duration: $duration
      inHome: $inHome
      invitedToVisit: $invitedToVisit
      offset: $offset
      requiresContinuityOfCare: $requiresContinuityOfCare
      preserveAutoschedulerGroup: $preserveAutoschedulerGroup
      providerTypeIds: $providerTypeIds
    ) {
      id
    }
  }
`);
